import { t } from "@redotech/redo-i18n";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import { finalSaleReturnFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  FinalSaleReturnState,
  isFinalSaleStepValid,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import EyeIcon from "@redotech/redo-web/icon-old/eye.svg";
import { produce } from "immer";
import * as React from "react";
import { StepDownstream, StepType, StepTypeDetailsProps } from "../step";

export const FINAL_SALE_RETURN = (
  Details: React.ComponentType<StepTypeDetailsProps<FinalSaleReturnState>>,
): StepType<FinalSaleReturnState, ModelStep.FinalSaleReturn> => ({
  Details: (props: StepTypeDetailsProps<FinalSaleReturnState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: STEP_TYPE_NAMES[ModelStep.FINAL_SALE_RETURN],
  valid(state) {
    return isFinalSaleStepValid(state).valid;
  },
  Icon: EyeIcon,
  iconColor: "success",
  description() {
    return t("Mark as final sale return");
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  empty: { next: undefined },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(
        `Invalid handle index ${handleIndex} for Final Sale Return step`,
      );
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  toModel(state, id) {
    return {
      type: ModelStep.FINAL_SALE_RETURN,
      next: state.next ? id(state.next) : undefined,
    };
  },
  fromModel(model, id) {
    return finalSaleReturnFromModel(model, id);
  },
});
